// src/components/Gantt/Gantt.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { gantt } from 'dhtmlx-gantt';
import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';
import GanttService from '../../services/ganttService';
import ganttDataUtilsService from '../../services/ganttDataUtilsService';
import './Gantt.css';

import cloneDeep from 'lodash.clonedeep';
import SkeletonGantt from './SkeletonGantt';
import { createRoot } from 'react-dom/client';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import type { Root } from 'react-dom/client';

declare const $: any;
declare const config: any;

export interface GanttConfig {
  apiUrl: string;
  authToken: string;
  selectedLayerId: number;
  WorkWeekend: boolean;
  isCacheEnabled?: boolean;
  cacheKey?: string;
  openInfoWindow?: (conversationId: number) => void;
  loadGanttLayers?: (layers: any[]) => void;
  saveGanttStorage?: (key: string, data: any) => void;
  getGanttStorage?: (key: string) => any;
  updateCacheDetails?: (key: string, conversations: any[]) => void;
  ReloadCustomFields?: (convId: number) => void;
}

export interface GanttProps {
  dashboard: any;
  config: GanttConfig;
}

const Gantt: React.FC<GanttProps> = props =>
{
  const ganttContainerRef = useRef<HTMLDivElement | null>(null);
  const currentPageRef = useRef(1);
  const waitingForMoreRef = useRef(false);
  const noMoreItemsRef = useRef(false);
  const requestPromiseRef = useRef<{ promise: Promise<any>; cancel: () => void } | null>(null);
  const eventsRef = useRef<string[]>([]);
  const dashboardStartDateRef = useRef<any>(undefined);
  const dashboardDueDateRef = useRef<any>(undefined);

  const [pageSize] = useState(40);
  const [countTask, setCountTask] = useState(0);
  const [, setData] = useState<any>({});
  const [isActive, setIsActive] = useState(false);
  const [initialData, setInitialData] = useState<any[]>([]);
  const [noData, setNoData] = useState(false);

  // Refs to hold latest state values for use in callbacks
  const countTaskRef = useRef(countTask);
  countTaskRef.current = countTask;
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;
  const propsRef = useRef(props);
  propsRef.current = props;

  const formattedDate = useCallback((d: Date): string =>
  {
    let month = String(d.getMonth() + 1);
    let day = String(d.getDate());
    const year = String(d.getFullYear());
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return month + '/' + day + '/' + year;
  }, []);

  const prepareRequest = useCallback(() =>
  {
    const p = propsRef.current;
    const requestConfig = {
      api: {
        url: p.config.apiUrl,
        token: p.config.authToken,
      },
      request: {
        ...p.dashboard,
        PageSize: pageSize,
        PageNumber: currentPageRef.current,
      },
    };
    return requestConfig;
  }, [pageSize]);

  const updateCache = useCallback(
    (
      ConversationId: number,
      LayerId: number,
      ValueSD: string,
      ValueDD: string,
      ValueE: any,
      ValueST: any,
    ) =>
    {
      const conversations = cloneDeep(initialDataRef.current);
      for (let a = 0; a < conversations.length; a++)
      {
        if (conversations[a].ConversationId == ConversationId)
        {
          for (let c = 0; c < conversations[a].CustomFieldLayers.length; c++)
          {
            if (conversations[a].CustomFieldLayers[c].LayerId == LayerId)
            {
              for (let x = 0; x < conversations[a].CustomFieldLayers[c].CustomFields.length; x++)
              {
                if (conversations[a].CustomFieldLayers[c].CustomFields[x].Name == 'Start date')
                {
                  conversations[a].CustomFieldLayers[c].CustomFields[x].Value = ValueSD;
                }
                else if (
                  conversations[a].CustomFieldLayers[c].CustomFields[x].Name == 'Due date'
                )
                {
                  conversations[a].CustomFieldLayers[c].CustomFields[x].Value = ValueDD;
                }
                else if (
                  conversations[a].CustomFieldLayers[c].CustomFields[x].Name == 'Estimated'
                )
                {
                  conversations[a].CustomFieldLayers[c].CustomFields[x].Value = ValueE;
                }
                else if (conversations[a].CustomFieldLayers[c].CustomFields[x].Name == 'Status')
                {
                  conversations[a].CustomFieldLayers[c].CustomFields[x].Value = ValueST;
                }
              }
              break;
            }
          }
          break;
        }
      }

      if (ganttDataUtilsService.props.config.updateCacheDetails)
      {
        ganttDataUtilsService.props.config.updateCacheDetails(
          ganttDataUtilsService.props.config.cacheKey,
          conversations,
        );
      }

      setInitialData(conversations);
    },
    [],
  );

  const save = useCallback(
    (
      dashboardid: number,
      conversationId: number,
      LayerId: number,
      nameField: string,
      basicFieldId: number,
      value: any,
      CustomFieldValueId: number,
      id: any,
    ) =>
    {
      let Conversation = ganttDataUtilsService.conversations.find(
        (x: any) => x.ConversationId == conversationId,
      );

      let indexConversation: number;

      if (!Conversation)
      {
        let flattenedConversations: any[] = [];
        for (let i = 0; i < ganttDataUtilsService.conversations.length; i++)
        {
          const topConversation = ganttDataUtilsService.conversations[i];
          const nestedPhaseConversations = topConversation.Conversations;
          flattenedConversations.push(topConversation);
          flattenedConversations = flattenedConversations.concat(nestedPhaseConversations);
        }
        Conversation = flattenedConversations.find((x: any) => x.ConversationId == conversationId);
        indexConversation = flattenedConversations.findIndex(
          (x: any) => x.ConversationId == conversationId,
        );
      }
      else
      {
        indexConversation = ganttDataUtilsService.conversations.findIndex(
          (x: any) => x.ConversationId == conversationId,
        );
      }

      const convId =
        Conversation.PhaseOfConversationId != null
          ? Conversation.PhaseOfConversationId
          : Conversation.ConversationId;

      const Layers = Conversation.CustomFieldLayers.find((x: any) => x.LayerId == LayerId);
      const indexLayer = Conversation.CustomFieldLayers.findIndex((x: any) => x.LayerId == LayerId);
      const CustomField = Layers.CustomFields.find(
        (x: any) =>
          x.Name == nameField &&
          x.BasicFieldId == basicFieldId &&
          x.CustomFieldValueId == CustomFieldValueId,
      );
      const indexCustomField = Layers.CustomFields.findIndex(
        (x: any) => x.Name == nameField && x.CustomFieldValueId == CustomFieldValueId,
      );
      if (!CustomField || CustomField.Value == value)
      {
        return;
      }
      CustomField.CoreTaskId = Conversation.CoreTaskId;
      CustomField.Value = value == null ? '' : value;
      if (Conversation.ConfigValueId == 9)
      {
        CustomField.ProjectId = Conversation.ProjectId;
      }

      if (
        CustomField.CustomFieldValueId === undefined ||
        CustomField.CustomFieldValueId === null ||
        CustomField.CustomFieldValueId === 0
      )
      {
        GanttService.savePost(CustomField).then(function (savedData: any)
        {
          let currentDashboard: any = localStorage.getItem(config?.appPrefix + '_currentDashboard');

          currentDashboard =
            JSON.parse(currentDashboard) != null ? JSON.parse(currentDashboard) : 0;
          if (dashboardid == currentDashboard.dashboardId || gantt.isTaskExists(id))
          {
            if (nameField == 'Start date')
            {
              gantt.getTask(id).b_start_date_cfvId = savedData.CustomFieldValueId;
            }
            else if (nameField == 'Due date')
            {
              gantt.getTask(id).b_due_date_cfvId = savedData.CustomFieldValueId;
            }
            else if (nameField == 'Estimated')
            {
              gantt.getTask(id).b_estimate_cfvId = savedData.CustomFieldValueId;
            }
            else if (nameField == 'Status')
            {
              gantt.getTask(id).b_status_cfvId = savedData.CustomFieldValueId;
            }
            gantt.updateTask(id);
            gantt.refreshData();
            CustomField.CustomFieldValueId = savedData.CustomFieldValueId;
            ganttDataUtilsService.updateCustomDetail(
              indexConversation,
              indexLayer,
              indexCustomField,
              CustomField,
            );
            setTimeout(function ()
            {
              ganttDataUtilsService.updateTaskCache(id);
              propsRef.current.config.ReloadCustomFields?.(convId);
            }, 0);
          }
        });
      }
      else
      {
        GanttService.save(CustomField).then(function ()
        {
          ganttDataUtilsService.updateCustomDetail(
            indexConversation,
            indexLayer,
            indexCustomField,
            CustomField,
          );
          setTimeout(function ()
          {
            ganttDataUtilsService.updateTaskCache(id);
            propsRef.current.config.ReloadCustomFields?.(convId);
          }, 0);
        });
      }
    },
    [],
  );

  const prepareToSave = useCallback(
    (id: any) =>
    {
      const p = propsRef.current;
      const dashboardid = p.dashboard.DashboardId;
      const isOneLayer = p.config.selectedLayerId > 0;
      if (gantt.getTask(id).parent == 0 && !isOneLayer) return;
      const a_start_date = formattedDate(gantt.getTask(id).start_date as Date);
      const a_due_date = formattedDate(gantt.getTask(id).end_date as Date);
      gantt.getTask(id).a_start_date = a_start_date;
      gantt.getTask(id).a_due_date = a_due_date;

      if (ganttDataUtilsService.props.config.isCacheEnabled)
      {
        updateCache(
          gantt.getTask(id).conversationId,
          gantt.getTask(id).layerId,
          gantt.getTask(id).a_start_date,
          gantt.getTask(id).a_due_date,
          gantt.getTask(id).estimate,
          gantt.getTask(id).status,
        );
      }
      save(
        dashboardid,
        gantt.getTask(id).conversationId,
        gantt.getTask(id).layerId,
        'Start date',
        gantt.getTask(id).b_start_date,
        a_start_date,
        gantt.getTask(id).b_start_date_cfvId,
        id,
      );
      save(
        dashboardid,
        gantt.getTask(id).conversationId,
        gantt.getTask(id).layerId,
        'Due date',
        gantt.getTask(id).b_due_date,
        a_due_date,
        gantt.getTask(id).b_due_date_cfvId,
        id,
      );
      save(
        dashboardid,
        gantt.getTask(id).conversationId,
        gantt.getTask(id).layerId,
        'Estimated',
        gantt.getTask(id).b_estimated,
        gantt.getTask(id).estimate,
        gantt.getTask(id).b_estimate_cfvId,
        id,
      );
      if (gantt.getTask(id).hasStatus)
      {
        save(
          dashboardid,
          gantt.getTask(id).conversationId,
          gantt.getTask(id).layerId,
          'Status',
          gantt.getTask(id).b_status,
          gantt.getTask(id).status,
          gantt.getTask(id).b_status_cfvId,
          id,
        );
      }
      gantt.updateTask(id);
      gantt.refreshData();
    },
    [formattedDate, save, updateCache],
  );

  const updateEstimate = useCallback(
    (id: any) =>
    {
      setTimeout(function ()
      {
        if (gantt.isTaskExists(id))
        {
          const hours = gantt.calculateDuration(
            gantt.getTask(id).start_date as Date,
            gantt.getTask(id).end_date as Date,
          );
          gantt.getTask(id).duration = hours;
          gantt.getTask(id).taskupdate = '';
          gantt.updateTask(id);
          gantt.refreshData();
          prepareToSave(id);
        }
      }, 100);
    },
    [prepareToSave],
  );

  const updateStatus = useCallback(
    (id: any) =>
    {
      setTimeout(function ()
      {
        if (gantt.isTaskExists(id) && gantt.getTask(id).hasStatus)
        {
          gantt.getTask(id).taskupdate = '';
          gantt.updateTask(id);
          gantt.refreshData();
          prepareToSave(id);
        }
      }, 100);
    },
    [prepareToSave],
  );

  const updateDrag = useCallback(
    (id: any) =>
    {
      setTimeout(function ()
      {
        if (gantt.isTaskExists(id))
        {
          gantt.getTask(id).taskupdate = '';
          if (!gantt.getTask(id).duration)
          {
            const endDate = cloneDeep(gantt.getTask(id).end_date) as Date;
            endDate.setHours(23, 0, 0, 0);
            const hours = gantt.calculateDuration(gantt.getTask(id).start_date as Date, endDate);
            gantt.getTask(id).duration = hours;
          }
          gantt.updateTask(id);
          gantt.refreshData();
          prepareToSave(id);
        }
      }, 100);
    },
    [prepareToSave],
  );

  const updateEndDate = useCallback(
    (id: any) =>
    {
      setTimeout(function ()
      {
        if (gantt.isTaskExists(id))
        {
          const endDate = cloneDeep(gantt.getTask(id).end_date) as Date;
          endDate.setHours(23, 0, 0, 0);
          const hours = gantt.calculateDuration(gantt.getTask(id).start_date as Date, endDate);
          gantt.getTask(id).end_date = endDate;
          gantt.getTask(id).duration = hours;
          gantt.getTask(id).taskupdate = '';
          gantt.updateTask(id);
          gantt.refreshData();
          prepareToSave(id);
        }
      }, 100);
    },
    [prepareToSave],
  );

  const updateStartDate = useCallback(
    (id: any) =>
    {
      setTimeout(function ()
      {
        if (gantt.isTaskExists(id))
        {
          const startDateDay = new Date(gantt.getTask(id).start_date as Date);
          const endDateDay = new Date(gantt.getTask(id).end_date as Date);

          if (!gantt.getTask(id).a_due_date || endDateDay.getDate() < startDateDay.getDate())
          {
            const endDate = new Date(gantt.getTask(id).start_date as Date);
            endDate.setHours(23, 0, 0, 0);
            endDate.setDate(endDate.getDate() + 1);
            const hours = gantt.calculateDuration(gantt.getTask(id).start_date as Date, endDate);
            gantt.getTask(id).end_date = endDate;
            gantt.getTask(id).start_date = gantt.getTask(id).start_date;
            gantt.getTask(id).duration = hours;
          }
          gantt.getTask(id).taskupdate = '';
          gantt.updateTask(id);
          gantt.refreshData();
          prepareToSave(id);
        }
      }, 100);
    },
    [prepareToSave],
  );

  const onAfterTaskUpdate = useCallback(
    (id: any, item: any) =>
    {
      switch (item.taskupdate)
      {
        case 'estimate':
          updateEstimate(id);
          break;
        case 'start_date':
          updateStartDate(id);
          break;
        case 'end_date':
          updateEndDate(id);
          break;
        case 'drag':
          updateDrag(id);
          break;
        case 'status':
          updateStatus(id);
          break;
        default:
          break;
      }
    },
    [updateEstimate, updateStartDate, updateEndDate, updateDrag, updateStatus],
  );

  const calculateTimeWeekend = useCallback((_idConv: any) =>
  {
    // Empty in legacy — placeholder
  }, []);

  const caculateTimeOnlyOpenRow = useCallback(() =>
  {
    const listRow = ganttDataUtilsService.ListOpenRow;
    if (listRow != null && listRow.length > 0)
    {
      for (let i = 0; i < listRow.length; i++)
      {
        if (listRow[i]?.openOrClose == true)
        {
          calculateTimeWeekend(listRow[i]!.idOpen);
        }
      }
    }
  }, [calculateTimeWeekend]);

  const taskClick = useCallback(
    (id: any, e: any) =>
    {
      const target = e?.target;
      if (!target) return;

      if (target.classList?.contains('gantt-task-title'))
      {
        const conversationId = parseInt(target.getAttribute('conversation-id'));
        if (propsRef.current.config.openInfoWindow)
        {
          propsRef.current.config.openInfoWindow(conversationId);
        }
      }
      else if (target.classList?.contains('gantt_open'))
      {
        calculateTimeWeekend(id);
        ganttDataUtilsService.updateOpenTask(id, true);
        gantt.getTask(id).$open = true;
        gantt.render();
        gantt.sort('order', false);
      }
      else if (target.classList?.contains('gantt_close'))
      {
        gantt.getTask(id).$open = false;
        ganttDataUtilsService.updateOpenTask(id, false);
        ganttDataUtilsService.updateParentTree(id);
        gantt.render();
      }
    },
    [calculateTimeWeekend],
  );

  const addMore = useCallback(
    (_left: number, _top: number, _scroll: number) =>
    {
      if (!waitingForMoreRef.current)
      {
        const selectedLayer = propsRef.current.config.selectedLayerId;
        waitingForMoreRef.current = true;
        currentPageRef.current++;
        const requestConfig = prepareRequest();
        requestPromiseRef.current = GanttService.getGantt(requestConfig.request);

        requestPromiseRef.current.promise.then(function (dataInput: any)
        {
          ganttDataUtilsService.appendDataFromResponse(
            countTaskRef.current,
            dataInput,
            selectedLayer,
            propsRef.current,
          );
          const info = ganttDataUtilsService.getData();
          const count = info.tasks.filter((x: any) => x.parent == 0);
          setCountTask(count.length);
          setData(info);

          if (count == info.tasks.length) noMoreItemsRef.current = true;

          requestPromiseRef.current = null;
          gantt.parse(info);
          gantt.sort('order', false);
          waitingForMoreRef.current = false;
        });
      }
    },
    [prepareRequest],
  );

  const ganttScroll = useCallback(
    (left: number, top: number) =>
    {
      if (top > 0)
      {
        const heighContainer =
          (document.querySelector('.gantt-container:not([style*="display: none"])') as HTMLElement)
            ?.offsetHeight ?? 0;
        const adjustedHeight = heighContainer - 31;
        const total_task = countTaskRef.current;
        const heighTotal = total_task * 22;
        const container = heighTotal - adjustedHeight;
        if (top > container)
        {
          let last_visible_task: any = 0;
          gantt.eachTask(function (task: any)
          {
            if (gantt.getTaskRowNode(task.id)) last_visible_task = task.id;
          });
          const ganttData = ganttDataUtilsService.getData();
          const task = ganttData.tasks.filter((x: any) => x.parent == 0);
          if (task.length > 0 && last_visible_task == task[total_task - 1]?.id)
          {
            if (!waitingForMoreRef.current && !noMoreItemsRef.current)
            {
              addMore(left, top, container);
            }
          }
        }
        else
        {
          noMoreItemsRef.current = false;
        }
      }
    },
    [addMore],
  );

  const updateSizeSkeleton = useCallback(() =>
  {
    const areatask = document.querySelector('.gantt_task') as HTMLElement;
    if (areatask)
    {
      const width = areatask.offsetWidth;
      if (width > 0)
      {
        const task = width / 5;
        document.querySelectorAll('.task_week_gantt').forEach(el =>
        {
          (el as HTMLElement).style.width = task + 'px';
        });
      }
    }
  }, []);

  const resetContainerSizing = useCallback(() =>
  {
    // Disable legacy autosize logic. We want the gantt container to always
    // fill the available flex space determined by the parent CSS layout.
    gantt.config.autosize = false;
  }, []);

  const updateDimensions = useCallback(() =>
  {
    // dhtmlx-gantt needs a concrete pixel height — percentage values don't work
    // because the library measures offsetHeight at init time.
    // We clear the inline height first so CSS flex-1 determines the natural size,
    // then capture that value and apply it explicitly for the library.
    const ganttEl = document.querySelector(
      '.gantt-container:not([style*="display: none"])',
    ) as HTMLElement;
    if (ganttEl)
    {
      ganttEl.style.height = '';
      const flexHeight = ganttEl.getBoundingClientRect().height;
      if (flexHeight > 0)
      {
        ganttEl.style.height = flexHeight + 'px';
      }
      else
      {
        // Fallback: compute from viewport if flex hasn't settled yet
        const rect = ganttEl.getBoundingClientRect();
        ganttEl.style.height = window.innerHeight - rect.top + 'px';
      }
    }
    document.querySelectorAll('.gantt-container-table').forEach(el =>
    {
      (el as HTMLElement).style.height = '100%';
    });

    setTimeout(function ()
    {
      if (propsRef.current.dashboard.DashboardViewTypeId == 7)
      {
        updateSizeSkeleton();
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        // Let dhtmlx-gantt recalculate its internal layout without
        // dispatching a resize event (which would re-trigger this handler).
        gantt.setSizes();
      }
      else
      {
        window.removeEventListener('resize', updateDimensions);
      }
    }, 1500);
  }, [updateSizeSkeleton]);

  const initializeConfiguration = useCallback(() =>
  {
    const p = propsRef.current;
    let isOneLayer = p.config.selectedLayerId > 0;

    let activeReactRoot: Root | null = null;

    (gantt.config as any).editor_types.custom_datepicker_editor = {
      show: function (id: any, column: any, _config: any, placeholder: any)
      {
        if (gantt.getTask(id).parent == 0 && isOneLayer == false) return;

        placeholder.innerHTML = '';

        let initialDate: Date | null = null;
        const currentTask = gantt.getTask(id);
        const sourceVal =
          column.name === 'start_date' ? currentTask.start_date : currentTask.end_date;
        if (sourceVal)
        {
          initialDate = sourceVal instanceof Date ? sourceVal : new Date(sourceVal);
          if (isNaN(initialDate.getTime())) initialDate = null;
        }

        activeReactRoot = createRoot(placeholder);

        const onChange = (date: Date | null) =>
        {
          if (date)
          {
            date.setHours(column.name === 'end_date' ? 23 : 0, 0, 0, 0);
            const t = gantt.getTask(id);
            if (column.name === 'start_date')
            {
              t.start_date = date;
            }
            else
            {
              t.end_date = date;
            }
            t.taskupdate = column.name;
            gantt.updateTask(id);
            gantt.refreshData();
          }
          gantt.ext.inlineEditors.hide();
        };

        const onBlur = () =>
        {
          // Slight delay prevents closing immediately when clicking the calendar popup itself
          setTimeout(() =>
          {
            gantt.ext.inlineEditors.hide();
          }, 150);
        };

        activeReactRoot.render(
          <div
            style={{
              padding: '0 4px',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <DatePicker
              selected={initialDate}
              onChange={onChange}
              onBlur={onBlur}
              dateFormat="MM/dd/yyyy"
              autoFocus={true}
              popperPlacement="bottom-start"
              popperProps={{ strategy: 'fixed' }}
              showPopperArrow={false}
              className="form-control"
              wrapperClassName="datepicker-wrapper"
              calendarClassName="custom-datepicker-calendar"
            />
          </div>,
        );
      },
      hide: function ()
      {
        if (activeReactRoot)
        {
          // Unmount asynchronously to avoid conflicts during event handling
          const rootToUnmount = activeReactRoot;
          setTimeout(() => rootToUnmount.unmount(), 0);
          activeReactRoot = null;
        }
      },
      set_value: function () {},
      get_value: function ()
      {
        return null;
      },
      is_changed: function ()
      {
        return false;
      },
      is_valid: function ()
      {
        return true;
      },
      focus: function () {},
    };

    const getInput = function (node: any)
    {
      return node.querySelector('input');
    };

    (gantt.config as any).editor_types.simpleNumber = {
      show: function (id: any, column: any, cfg: any, placeholder: any)
      {
        if (gantt.getTask(id).parent == 0 && isOneLayer == false) return;
        const min = cfg.min || 0;
        const max = cfg.max || 100;
        const html =
          "<div><input type='number' min='" +
          min +
          "' max='" +
          max +
          "' name='" +
          column.name +
          "' ></div>";
        placeholder.innerHTML = html;
      },
      hide: function () {},
      set_value: function (value: any, id: any, _column: any, node: any)
      {
        if (gantt.getTask(id).parent == 0 && isOneLayer == false) return;
        getInput(node).value = value;
      },
      get_value: function (id: any, _column: any, node: any)
      {
        if (gantt.getTask(id).parent == 0 && isOneLayer == false) return;
        return getInput(node).value || '';
      },
      is_changed: function (value: any, id: any, column: any, node: any)
      {
        const currentValue = this.get_value(id, column, node);
        if (gantt.getTask(id).parent == 0 && isOneLayer == false) return;
        if (Number(value) !== Number(currentValue))
        {
          gantt.getTask(id).taskupdate = 'estimate';
          gantt.updateTask(id);
          gantt.refreshData();
        }
        return Number(value) !== Number(currentValue);
      },
      is_valid: function (value: any)
      {
        return !isNaN(parseInt(value, 10));
      },
      focus: function (node: any)
      {
        const input = getInput(node);
        if (!input) return;
        if (input.focus) input.focus();
        if (input.select) input.select();
      },
    };

    const getSelectInput = function (node: any)
    {
      return node.querySelector('select');
    };
    (gantt.config as any).editor_types.custom_select = {
      show: function (id: any, _column: any, _cfg: any, placeholder: any)
      {
        const basicfieldid = gantt.getTask(id).b_status;
        const statusOptions = ganttDataUtilsService.statusOptions.filter(
          x => x.basicfieldid == basicfieldid,
        );
        const options = statusOptions || [];
        let result = '<div><select>';
        const arrayOptions: string[] = [];
        if (options.length == 0)
        {
          result = '';
          placeholder.innerHTML = result;
        }
        else
        {
          for (let s = 0; s < options.length; s++)
          {
            arrayOptions.push(
              "<option value='" + options[s]!.key + "'>" + options[s]!.label + '</option>',
            );
          }
          result += arrayOptions.join('') + '</select></div>';
          placeholder.innerHTML = result;
        }
      },
      set_value: function (value: any, id: any, _column: any, node: any)
      {
        if (gantt.getTask(id).parent == 0 && isOneLayer == false) return;
        if (!getInput(node)) return;
        getSelectInput(node).value = value;
      },
      get_value: function (id: any, _column: any, node: any)
      {
        if (gantt.getTask(id).parent == 0 && isOneLayer == false) return;
        if (!getSelectInput(node)) return;
        return getSelectInput(node).value || '';
      },
      is_changed: function (_value: any, id: any)
      {
        if (gantt.getTask(id).parent == 0 && isOneLayer == false) return;
        gantt.getTask(id).taskupdate = 'status';
        gantt.updateTask(id);
        gantt.refreshData();
        return true;
      },
      save: function () {},
      focus: function () {},
    };

    const dateStartEditor = { type: 'custom_datepicker_editor', map_to: 'start_date' };
    const dateEndEditor = { type: 'custom_datepicker_editor', map_to: 'end_date' };
    const numberEditor = { type: 'simpleNumber', map_to: 'estimate', min: 0, max: 9999 };
    const statusEditor = { type: 'custom_select', map_to: 'status' };

    isOneLayer = false;

    // Columns config (always multi-layer mode for columns)
    gantt.config.columns = [
      {
        name: 'text',
        label: 'Project/Task name',
        width: 200,
        tree: true,
        template: function (task: any)
        {
          return task.render;
        },
      },
      {
        name: 'start_date',
        label: 'Start date',
        width: 100,
        align: 'center',
        editor: dateStartEditor,
        template: function (task: any)
        {
          if (task.$open) return '';
          else if (task.a_start_date == '') return '';
          else return task.start_date;
        },
      },
      {
        name: 'end_date',
        label: 'Due date',
        width: 100,
        align: 'center',
        editor: dateEndEditor,
        template: function (task: any)
        {
          if (task.$open) return '';
          else if (task.a_due_date == '') return '';
          else return task.end_date;
        },
      },
      {
        name: 'estimate',
        label: 'Estimated(Hrs)',
        width: 100,
        align: 'center',
        editor: numberEditor,
        template: function (task: any)
        {
          if (task.$open) return '';
          else if (task.estimate == 0) return 0;
          else if (task.estimate == '') return '';
          else return task.estimate;
        },
      },
      {
        name: 'status',
        label: 'Status',
        width: 100,
        align: 'center',
        editor: statusEditor,
        template: function (task: any)
        {
          const options = ganttDataUtilsService.statusOptions.find(x => task.status == x.key);
          if (task.hasStatus == false) return 'N/A';
          if (task.$open) return '';
          else if (task.status == '') return '';
          else
          {
            return options ? options.label : task.statusText;
          }
        },
      },
    ] as any;

    gantt.templates.task_text = function ()
    {
      return '';
    };
    gantt.config.initial_scroll = false;

    gantt.templates.task_class = function (_start: any, _end: any, task: any)
    {
      if (
        task.$open ||
        task.estimate == 0 ||
        task.estimate == '' ||
        task.duration == 0 ||
        task.duration == ''
      )
      {
        return 'NoShowBar';
      }
      else
      {
        return '';
      }
    };

    gantt.config.scales = [
      { unit: 'month', step: 1, format: '%M, %Y' },
      {
        unit: 'day',
        step: 1,
        format: '%j %D',
        css: function (date: Date)
        {
          const day = date.getDay();
          const isWeekend = day === 6 || day === 0;
          if (isWeekend && propsRef.current.config.WorkWeekend == false)
          {
            return 'week-end';
          }
          else
          {
            return '';
          }
        },
      },
    ];

    gantt.templates.grid_folder = function ()
    {
      return '';
    };
    gantt.templates.grid_file = function ()
    {
      return '';
    };

    gantt.templates.grid_open = function (item: any)
    {
      const list = ganttDataUtilsService.ListOpenRow;
      if (list != null && list.length > 0)
      {
        const id = parseInt(item.id);
        const openRow = list.find(x => x.idOpen == id);
        if (openRow != undefined)
        {
          item.$open = openRow.openOrClose;
        }
      }
      return "<div class='gantt_tree_icon gantt_" + (item.$open ? 'close' : 'open') + "'></div>";
    };

    gantt.plugins({
      marker: true,
      keyboard_navigation: true, // Required for inline editors
      undo: true,
    });

    // Start inline editing on click
    gantt.ext.inlineEditors.attachEvent('onEditStart', function (_state: any)
    {
      // Return true to allow edit
      return true;
    });

    gantt.addMarker({ start_date: new Date(), css: 'today', title: '' });

    gantt.config.duration_unit = 'hour';
    gantt.config.grid_width = 470;
    if ((gantt as any).env?.isFF && parseInt(navigator.userAgent.split('Firefox/')[1]!) > 87)
    {
      gantt.config.wheel_scroll_sensitivity = 0.05;
    }
    gantt.config.work_time = true;
    gantt.config.skip_off_time = true;
    gantt.config.select_task = false;
    gantt.config.scroll_size = 20;
    gantt.setWorkTime({ day: 1, hours: [0, 24] });
    gantt.setWorkTime({ day: 2, hours: [0, 24] });
    gantt.setWorkTime({ day: 3, hours: [0, 24] });
    gantt.setWorkTime({ day: 4, hours: [0, 24] });
    gantt.setWorkTime({ day: 5, hours: [0, 24] });
    gantt.setWorkTime({ day: 6, hours: [0, 24] });
    gantt.setWorkTime({ day: 0, hours: [0, 24] });

    gantt.config.round_dnd_dates = false;
    (gantt.config.columns as any)[0].sort = 'order';

    const events: string[] = [];

    const onGanttRender = gantt.attachEvent('onGanttRender', function ()
    {
      const els = document.querySelectorAll(
        "[aria-level='0'] [data-column-index='4']:not(:first-child)",
      );
      els.forEach(el =>
      {
        el.classList.add('enable-help-tooltip');
        el.setAttribute('data-title', 'Expand to see status');
      });
    });

    const onTaskClick = gantt.attachEvent('onTaskClick', taskClick as any);
    const onGanttScroll = gantt.attachEvent('onGanttScroll', ganttScroll);
    const onBeforeGanttRender = gantt.attachEvent('onBeforeGanttRender', resetContainerSizing);
    const onBeforeDataRender = gantt.attachEvent('onBeforeDataRender', resetContainerSizing);
    const onTaskDrag = gantt.attachEvent('onTaskDrag', function () {});
    const onAfterTaskDrag = gantt.attachEvent('onAfterTaskDrag', function (id: any)
    {
      gantt.getTask(id).taskupdate = 'drag';
      gantt.updateTask(id);
      gantt.refreshData();
    });
    const onAfterTaskMove = gantt.attachEvent('onAfterTaskMove', function (id: any)
    {
      gantt.getTask(id).taskupdate = 'drag';
      gantt.updateTask(id);
      gantt.refreshData();
    });
    const onAfterTaskUpdateEvt = gantt.attachEvent('onAfterTaskUpdate', onAfterTaskUpdate);
    const onBeforeTaskDrag = gantt.attachEvent('onBeforeTaskDrag', function (id: any)
    {
      const oneLayer = propsRef.current.config.selectedLayerId > 0;
      if (gantt.getTask(id).parent == 0 && !oneLayer) return false;
      return true;
    });
    // Removed duplicate onAfterTaskUpdate handler — updateStatus is already
    // called via onAfterTaskUpdate → onAfterTaskUpdate callback above.

    events.push(onTaskClick);
    events.push(onGanttScroll);
    events.push(onBeforeGanttRender);
    events.push(onBeforeDataRender);
    events.push(onTaskDrag);
    events.push(onAfterTaskDrag);
    events.push(onAfterTaskMove);
    events.push(onAfterTaskUpdateEvt);
    events.push(onBeforeTaskDrag);

    events.push(onGanttRender);

    eventsRef.current = events;

    gantt.config.details_on_dblclick = false;
    gantt.config.show_unscheduled = true;
    gantt.config.readonly = false;
    gantt.config.date_format = '%m/%d/%Y %H:%i';
    gantt.config.date_grid = '%m/%d/%Y';
    gantt.config.row_height = 22;
    gantt.config.scale_height = 31;
    gantt.config.fit_tasks = true;

    const start_date = new Date();
    const end_date = new Date();
    start_date.setHours(0, 0, 0, 0);
    end_date.setDate(start_date.getDate() + 4);
    gantt.config.start_date = start_date;
    gantt.config.end_date = end_date;
    gantt.config.show_tasks_outside_timescale = true;

    // Ensure the container has a real pixel height before gantt.init()
    // to prevent the "container has small height" dhtmlx-gantt warning.
    updateDimensions();
    gantt.init(ganttContainerRef.current!);
    updateSizeSkeleton();
  }, [
    updateCache,
    save,
    taskClick,
    ganttScroll,
    resetContainerSizing,
    onAfterTaskUpdate,
    updateStatus,
    updateSizeSkeleton,
    updateDimensions,
  ]);

  const loadCache = useCallback((dataInput: any) =>
  {
    const p = propsRef.current;
    const selectedLayer = p.config.selectedLayerId;

    if (p.dashboard.DashboardId > 0)
    {
      gantt.clearAll();
      if (dashboardStartDateRef.current && dashboardDueDateRef.current)
      {
        gantt.config.start_date = new Date(dashboardStartDateRef.current);
        gantt.config.end_date = new Date(dashboardDueDateRef.current);
      }
      gantt.refreshData();
      ganttDataUtilsService.getDataFromDashboardResponse(dataInput, selectedLayer, p);
      const info = ganttDataUtilsService.getData();
      const count = info.tasks.filter((x: any) => x.parent == 0);

      const convs = cloneDeep(ganttDataUtilsService.conversations);
      setCountTask(count.length);
      setData(info);
      setInitialData(convs);
      setNoData(count.length <= 0);

      if (p.config.loadGanttLayers) p.config.loadGanttLayers(ganttDataUtilsService.layerDropdown);

      gantt.parse(info);
      requestAnimationFrame(() =>
      {
        gantt.setSizes();
        gantt.render();
        gantt.scrollTo(0, 0);
      });
    }
  }, []);

  const loadData = useCallback(
    (cacheData?: any) =>
    {
      const p = propsRef.current;
      const selectedLayer = p.config.selectedLayerId;
      const requestConfig = prepareRequest();

      const processData = function (dataInput: any)
      {
        let isSameData = false;
        if (p.config.isCacheEnabled && cacheData && cacheData != null)
        {
          if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1)
          {
            if (dataInput.Conversations && dataInput.Conversations.length > 0)
            {
              dataInput.Conversations = dataInput.Conversations.sort(function (a: any, b: any)
              {
                return new Date(a.UpdatedAt).getTime() - new Date(b.UpdatedAt).getTime();
              });
            }

            if (cacheData.Conversations && cacheData.Conversations.length > 0)
            {
              cacheData.Conversations = cacheData.Conversations.sort(function (a: any, b: any)
              {
                return new Date(a.UpdatedAt).getTime() - new Date(b.UpdatedAt).getTime();
              });
            }
          }
          isSameData = JSON.stringify(cacheData) == JSON.stringify(dataInput);
        }
        if (!isSameData)
        {
          gantt.clearAll();
          gantt.refreshData();
          ganttDataUtilsService.getDataFromDashboardResponse(dataInput, selectedLayer, p);
          const info = ganttDataUtilsService.getData();
          const count = info.tasks.filter((x: any) => x.parent == 0);
          const convs = cloneDeep(ganttDataUtilsService.conversations);
          setCountTask(count.length);
          setData(info);
          setInitialData(convs);
          setNoData(count.length <= 0);

          if (p.config.loadGanttLayers)
            p.config.loadGanttLayers(ganttDataUtilsService.layerDropdown);

          gantt.parse(info);
          gantt.sort('order', false);
          // Force layout recalculation after parsing data
          requestAnimationFrame(() =>
          {
            gantt.setSizes();
            gantt.render();
            gantt.scrollTo(0, 0);
          });

          if (p.config.isCacheEnabled && p.config.saveGanttStorage)
          {
            p.config.saveGanttStorage(p.config.cacheKey!, dataInput);
          }
        }

        if (cacheData != null)
        {
          caculateTimeOnlyOpenRow();
        }
      };

      if (p.dashboard.DashboardId > 0)
      {
        ganttDataUtilsService.layersToHide = p.dashboard.LayersToHide || '';
        requestPromiseRef.current = GanttService.getGantt(requestConfig.request);

        requestPromiseRef.current.promise.then(function (response: any)
        {
          if (response == null)
          {
            setIsActive(true);
            setNoData(true);
          }
          else if (response.Conversations == null)
          {
            setIsActive(true);
            setNoData(true);
          }
          else if (response.Conversations.length == 0)
          {
            setIsActive(true);
            setNoData(true);
          }
          else
          {
            setIsActive(true);
            if (!response)
            {
              return;
            }
            requestPromiseRef.current = null;
            processData(response);
          }
        });
      }
    },
    [prepareRequest, caculateTimeOnlyOpenRow],
  );

  const initilizeData = useCallback(() =>
  {
    const p = propsRef.current;

    if (p.config.isCacheEnabled && p.config.getGanttStorage)
    {
      const cacheData = p.config.getGanttStorage(p.config.cacheKey!);

      if (cacheData && cacheData != null)
      {
        setIsActive(true);
        loadCache(cacheData);
      }

      loadData(cacheData);
    }
    else
    {
      loadData();
    }
  }, [loadCache, loadData]);

  // componentDidMount equivalent
  useEffect(() =>
  {
    GanttService.getDashboardInformationById(props.dashboard.DashboardId).then(
      (dashboardInfo: any) =>
      {
        dashboardStartDateRef.current = dashboardInfo.StartDate
          ? dashboardInfo.StartDate
          : undefined;
        const dueDate = dashboardInfo.DueDate ? new Date(dashboardInfo.DueDate) : undefined;
        if (dueDate)
        {
          dueDate.setDate(dueDate.getDate() + 1);
        }
        dashboardDueDateRef.current = dueDate;

        gantt.clearAll();
        window.addEventListener('resize', updateDimensions);

        updateDimensions();
        initializeConfiguration();
        initilizeData();
        gantt.sort('order', false);

        if (dashboardStartDateRef.current && dashboardDueDateRef.current)
        {
          gantt.config.start_date = new Date(dashboardStartDateRef.current);
          gantt.config.end_date = new Date(dashboardDueDateRef.current);
          gantt.render();
        }
      },
    );

    // componentWillUnmount equivalent
    return () =>
    {
      window.removeEventListener('resize', updateDimensions);
      gantt.clearAll();
      for (let i = 0; i < eventsRef.current.length; i++)
      {
        gantt.detachEvent(eventsRef.current[i]!);
      }
      eventsRef.current = [];
      if (requestPromiseRef.current) requestPromiseRef.current.cancel();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const name = 'xxx_gantt_' + props.dashboard.DashboardId;

  return (
    <div style={{ width: '100%', height: '100%' }}>
      {noData && (
        <p className="noDataSpreadsheet">This dashboard doesn&apos;t have any data to display.</p>
      )}
      {!noData && (
        <div className="gantt-container-table">
          <div
            id={name}
            ref={ganttContainerRef}
            style={{ width: '100%', height: '100%', minHeight: '200px' }}
          ></div>

          {!isActive && (
            <div className="gantt-skeleton-overlay">
              <SkeletonGantt />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Gantt;
